<template>
  <div v-if="rights"
    :class="{
      'page-hina': isHina,
      'page-jps': isJps,
    }"
  >
    <button
      ref="showRightsButton"
      class="jps-rights-tag"
      :class="{ 'jps-rights-tag-simple': simple, 'jps-rights-tag-large': large }"
      @click="showRights()"
    >
      <BaseIcon v-if="!simple" name="icon/license-contents" alt="License of Contents" />
      <slot v-bind="{ rights, label: lc(rightsTypeCode, rights) }">{{
        lc(rightsTypeCode, rights)
      }}</slot>
    </button>
    <UiContentModal
      ref="contentModal"
      has-bg-color="gray"
    >
      <RightsPopup
        :rights="rights"
        :rights-description="rightsDescription"
        :type="type"
        :data="data"
      />
    </UiContentModal>
    <UiContentTooltip
      ref="contentTooltip"
      tooltip-class="jps-rights-span-tooltip"
      placement="bottom"
      :other-toggle="showRightsButton">
      <RightsPopup
        :rights="rights"
        :rights-description="rightsDescription"
        :type="type"
        :data="data"
      />
    </UiContentTooltip>
  </div>
</template>

<script lang="ts" setup>
import type { UiContentModal, UiContentTooltip } from '#components'

const { lc } = useI18n()
const { $getCodeRightsType } = useNuxtApp()
const { $isMobile } = useNuxtApp()
const { isHina, isJps } = useServiceType()

interface Props {
  simple?: boolean
  large?: boolean
  rights?: string
  rightsDescription?: LangString
  type?: Key
  data?: { id: string }
}
const props = withDefaults(defineProps<Props>(), {})

const rightsTypeCode: Code = $getCodeRightsType()

const showRightsButton: Ref<HTMLElement> = ref()
const contentModal = ref<InstanceType<typeof UiContentModal>>()
const contentTooltip = ref<InstanceType<typeof UiContentTooltip>>()
const showRights = async () => {
  if ($isMobile.value) {
    try {
      await contentModal.value.openAwait()
    } catch (cancel) {}
  } else {
    contentTooltip.value.toggle()
  }
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.jps-rights-span-tooltip {
  background: $jps-gray-50 !important;
  .jps-tooltip-arrow::before {
    background: $jps-gray-50 !important;
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-rights-tag {
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  border: 1px solid $jps-gray-300;
  border-radius: 0.25rem;
  background: $jps-sub-blue;
  color: white;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.64, 0.09, 0.08, 1);
  transition-duration: 250ms;
  padding: 0.5rem 0.75rem 0.5rem;
  margin-top: 0.25rem;
  svg {
    color: $white;
    margin-right: 0.25rem;
  }
  &.jps-rights-tag-large {
    font-size: 1rem;
    padding: 0.75rem;
    img {
      height: 1.5rem;
      margin-right: 0.5rem;
    }
  }
  &:hover {
    background-color: #6c839f;
    cursor: pointer;
  }
}

.ui-rights-tag-popup {
  background-color: $jps-gray-50;
}

.page-hina {
  .jps-rights-tag {
    background: $hina-accent-lime;
    color: $black;
    border-color: $hina-accent-lime;
    border-radius: 0.125rem;

    &.jps-rights-tag-large {
      padding: 0.5rem 0.25rem;
    }

    &:hover {
      background: $hina-accent-lime;
      border-color: $hina-accent-green;
    }

    svg {
      color: $jps-gray-700;
    }
  }
}

// スタイルの上書きを含むため、末尾に記載する。
.page-hina,
.page-jps {
  .jps-rights-tag-simple {
    text-align: left;
    background-color: transparent;
    border: none;
    color: $jps-sub-blue;
    box-shadow: none;
    padding: 0;
    &:hover {
      color: $jps-key-blue-hover;
      cursor: pointer;
      background-color: transparent;
    }
  }
}
</style>
